import MicrolessonsApi from '../api/MicrolessonsApi';
import {
  LessonGenerationFromPromptModel,
  LessonGenerationModel,
} from '../models/AI/LessonGenerationModel';
import {MicrolessonGenerationCostModel} from '../models/Microlessons/MicrolessonGenerationCostModel';
import {CreateMicrolessonModel} from '../models/Microlessons/Microlesson';

class MicrolessonsRepository {
  private microlessonsApi = new MicrolessonsApi();

  public generateLesson(organizationId: string, model: LessonGenerationModel) {
    return this.microlessonsApi.genrateLessonWithAI(organizationId, model);
  }

  public async generateLessonFromSlide(
    organizationId: string,
    model: {
      libraryMediaFileId: string;
      addBranching: boolean;
      addAssessment: boolean;
      passingScore?: number;
      language: string;
      dialect: string;
      name: string;
    },
  ) {
    return this.microlessonsApi.generateLessonFromSlide(organizationId, model);
  }

  public async genrateLessonFromPromptWithAI(
    organizationId: string,
    model: LessonGenerationFromPromptModel,
  ) {
    return this.microlessonsApi.genrateLessonFromPromptWithAI(
      organizationId,
      model,
    );
  }

  public async getMicrolessonsList(
    organizationId: string,
    page: number,
    pageSize: number,
    search?: string,
  ) {
    return this.microlessonsApi.getMicrolessonsList(
      organizationId,
      page,
      pageSize,
      search,
    );
  }

  public async getMicrolessonById(organizationId: string, lessonId: string) {
    return this.microlessonsApi.getMicrolessonById(organizationId, lessonId);
  }

  public async getMicrolessonPreview(organizationId: string, lessonId: string) {
    return this.microlessonsApi.getMicrolessonPreview(organizationId, lessonId);
  }

  public async createMicrolesson(
    organizationId: string,
    model: CreateMicrolessonModel,
    avatar?: File,
  ) {
    const result = await this.microlessonsApi.createMicrolesson(
      organizationId,
      model,
    );

    if (result.success && result.data && avatar) {
      await this.microlessonsApi.uploadMicrolessonAvatar(
        organizationId,
        result.data.id,
        avatar,
      );
    }

    return result;
  }

  public async editMicrolesson(
    organizationId: string,
    lessonId: string,
    model: CreateMicrolessonModel,
    avatar?: File,
  ) {
    const result = await this.microlessonsApi.editMicrolesson(
      organizationId,
      lessonId,
      model,
    );
    if (result.success && avatar) {
      await this.microlessonsApi.uploadMicrolessonAvatar(
        organizationId,
        lessonId,
        avatar,
      );
    }
    return result;
  }

  public async getMicrolessonsLibrary(
    organizationId: string,
    page: number,
    pageSize: number,
    isGradable?: boolean,
    search?: string,
  ) {
    return this.microlessonsApi.getMicrolessonsLibrary(
      organizationId,
      page,
      pageSize,
      isGradable,
      search,
    );
  }

  public async copyMicrolessonToCourse(
    organizationId: string,
    courseId: string,
    folderId: string,
    microlessonId: string,
  ) {
    return this.microlessonsApi.copyMicrolessonToCourse(
      organizationId,
      courseId,
      folderId,
      microlessonId,
    );
  }

  public async deleteMicrolesson(
    organizationId: string,
    microlessonId: string,
  ) {
    return this.microlessonsApi.deleteMicrolesson(
      organizationId,
      microlessonId,
    );
  }

  public async getMicrolessonCostEstimation(
    organizationId: string,
    model: MicrolessonGenerationCostModel,
  ) {
    return this.microlessonsApi.getMicrolessonCostEstimation(
      organizationId,
      model,
    );
  }

  public async checkGenerationAvailability(organizationId: string) {
    return this.microlessonsApi.checkGenerationAvailability(organizationId);
  }
}

export default MicrolessonsRepository;
